'use client'

import React from 'react'
import styles from './styles.module.css'
import SmartLink from '../../../SmartLink'
import SaleCountdown from '../../../SaleCampaign/SaleCountdown'
import { pathWithRegion } from '../../../../utils/regions'
import { buildCheckoutPageUrlFromProductSetId } from '../../../../utils/shopItems'
import { useRegionOffers } from '../../../RegionOffers'
import { itemTypeOf } from '../saleFormat'
import { getCampaignPhase, getDealGroups, PHASE_LIVE, PHASE_TEASER, msOf } from '../../../../lib/saleCampaign'

// Strip at the top of /<region>/shop while the sale runs: the sale name and a
// link to the full sale page on top, then one card per deal set (pedals,
// wheels) with its product photos and its own countdown (to its end, or to its
// start if it is still to come). The clock is owned by the Shop island (passed
// in) so the shop has one timer.
function SaleShopBanner({ campaign, nowMs, mounted, region }) {
  const { offers } = useRegionOffers()
  const phase = getCampaignPhase(campaign, nowMs)
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return null
  const live = phase === PHASE_LIVE
  // Region overrides (`byRegion.<region>`) on top, e.g. US copy without the
  // Lite Formula wheel, which has no deal there.
  const baseCopy = campaign.shopBanner || {}
  const copy = { ...baseCopy, ...((baseCopy.byRegion && region && baseCopy.byRegion[region]) || {}) }
  const href = pathWithRegion(`/${campaign.route}`, region)
  const groups = getDealGroups(campaign, nowMs, region)
  // Each set's card opens its product's shop page (the set's first deal: the
  // Throttle & Brake for the pedal deals); the sale page if the offer is unknown.
  const productHrefOf = (g) => {
    const deal = g.deals && g.deals[0]
    const item = deal && (offers || []).find((o) => itemTypeOf(o) === deal.itemType)
    return item && item.productSetId ? buildCheckoutPageUrlFromProductSetId(item.productSetId, region) : href
  }
  const sets = groups.length ? groups : [{ key: 'sale', live, targetMs: live ? msOf(campaign.end) : msOf(campaign.start), images: [] }]

  return (
    <div className={styles.banner}>
      <div className={styles.head}>
        <div className={styles.text}>
          <span className={styles.kicker}>{live ? (copy.kickerLive || `${campaign.name} is live`) : (copy.kickerTeaser || `${campaign.name} starts soon`)}</span>
          <span className={styles.line}>
            {live
              ? (copy.lineLive || 'Sale prices are on every card with the sale tag.')
              : (copy.lineTeaser || 'All deals are published. See them now.')}
          </span>
        </div>
        <SmartLink href={href} className={styles.cta}>{live ? (copy.ctaLive || 'All deals') : (copy.ctaTeaser || 'See the deals')} →</SmartLink>
      </div>
      <div className={styles.timers}>
        {sets.map((g) => (
          <SmartLink key={g.key} href={g.deals ? productHrefOf(g) : href} className={styles.timer}>
            {g.images.length > 0 && (
              <span className={styles.photos}>
                {g.images.map((img) => {
                  // The full-width still (2.2:1) across the top of the card.
                  return (
                    <span key={img.src} className={styles.photo}>
                      {/* eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component -- fixed-ratio campaign still, sized box: no layout shift */}
                      <img src={img.src} alt={img.alt || ''} width={img.width || 880} height={img.height || 400} loading="lazy" decoding="async" />
                    </span>
                  )
                })}
              </span>
            )}
            <span className={styles.timerBody}>
              {(g.live ? g.countdownLive : g.countdownTeaser) && (
                <span className={styles.timerLabel}>{g.live ? g.countdownLive : g.countdownTeaser}</span>
              )}
              <SaleCountdown targetMs={g.targetMs} nowMs={nowMs} mounted={mounted} size="md" theme="dark" showDays live/>
            </span>
          </SmartLink>
        ))}
      </div>
    </div>
  )
}

export default SaleShopBanner
